import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import IdleTimeoutWarning, { useIdleTimeout } from './components/IdleTimeoutWarning';
import AppHeader from './components/AppHeader';
import { HeaderProvider } from './contexts/HeaderContext';
import {
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import ProviderRegistration from './components/ProviderRegistration';
import CoachDashboard from './components/CoachDashboard';
import ClientDashboard from './components/ClientDashboard';
import FillProfile from './components/FillProfile';
import SetupPage from './components/SetupPage';
import SigningPage from './components/SigningPage';
import BookingPage from './components/BookingPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboard from './modules/dashboard/admin/AdminDashboard';
import DemoBookingPage from './pages/DemoBookingPage';
import CoachBookingPage from './pages/CoachBookingPage';
import { registrationService } from './services/registrationService';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { config } from './config/environment';
import VerifyAccountPage from './components/VerifyAccountPage';
import CheckInPublicForm from './components/CheckInPublicForm';
import TermsOfUsePage from './pages/TermsOfUsePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AIUsageDataPolicyPage from './pages/AIUsageDataPolicyPage';
import HelpPage from './pages/HelpPage';
import HelpWidget from './components/HelpWidget';
import InvoicePublicPage from './pages/InvoicePublicPage';
import ActionItemReviewPage from './pages/ActionItemReviewPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import SupportPage from './pages/SupportPage';
import MarketingLandingPage from './pages/MarketingLandingPage';
import TourPage from './pages/TourPage';
import LoginPage from './pages/LoginPage';
import StripeReturn from './pages/StripeReturn';
import ClientDetailPage from './pages/ClientDetailPage';
import SessionCockpitPage from './pages/SessionCockpitPage';
import SessionDashboardPage from './pages/SessionDashboardPage';
import SessionJoinPage from './pages/SessionJoinPage';


function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('authToken');
  if (!token) return <Navigate to="/login" replace />;
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  if (userData.must_verify) return <Navigate to="/verify" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('authToken');
  const userType = localStorage.getItem('userType');
  if (!token || userType !== 'admin') return <Navigate to="/admin/login" replace />;
  return <>{children}</>;
}

/** Blocks client access until verified (if required) and profile is complete. */
function ClientRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('authToken');
  if (!token) return <Navigate to="/login" replace />;
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  if (userData.must_verify) return <Navigate to="/verify" replace />;
  if (userData.must_reset_password) return <Navigate to="/fill-profile" replace />;
  return <>{children}</>;
}

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const isFirstLogin = searchParams.get('first_login') === 'true';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showResetPw, setShowResetPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token && !isFirstLogin) {
    return (
      <div className="min-h-screen bg-dark-canvas flex items-center justify-center px-6">
        <div className="bg-dark-card rounded-xl p-8 shadow-lg max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Invalid Reset Link</h2>
          <p className="text-gray-300 mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/" className="btn-primary inline-block">Back to Login</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 12) { setError('Password must be at least 12 characters.'); return; }
    if (!/[A-Z]/.test(newPassword)) { setError('Password must contain at least one uppercase letter.'); return; }
    if (!/[a-z]/.test(newPassword)) { setError('Password must contain at least one lowercase letter.'); return; }
    if (!/[0-9]/.test(newPassword)) { setError('Password must contain at least one number.'); return; }
    if (!/[^A-Za-z0-9]/.test(newPassword)) { setError('Password must contain at least one special character.'); return; }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      let result: { success: boolean; message?: string };
      if (isFirstLogin) {
        // Use JWT already in localStorage — no reset token needed
        const authToken = localStorage.getItem('authToken');
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
        const resp = await fetch(`${apiBase}/auth/change-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({ new_password: newPassword }),
        });
        const body = await resp.json();
        if (!resp.ok) result = { success: false, message: (body.detail as string) || 'Change failed.' };
        else result = { success: true };
      } else {
        result = await registrationService.resetPassword(token!, newPassword);
      }
      if (result.success) {
        setSuccess(true);
        setTimeout(() => navigate(isFirstLogin ? '/dashboard/client' : '/'), 2000);
      } else {
        setError(result.message || 'Reset failed. The link may have expired.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-canvas flex items-center justify-center px-6">
      <div className="bg-dark-card rounded-xl p-8 shadow-lg max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyIcon className="w-8 h-8 text-primary-600" />
          </div>
          <h2 className="text-2xl font-bold text-white">
            {isFirstLogin ? 'Create Your Password' : 'Set New Password'}
          </h2>
          <p className="text-gray-300 mt-1">
            {isFirstLogin
              ? 'Welcome! Please create a personal password before continuing.'
              : 'Enter your new password below.'}
          </p>
        </div>

        {success ? (
          <div className="p-4 bg-green-900/30 border border-green-700 rounded-lg text-center">
            <p className="text-green-300 font-medium">
              {isFirstLogin ? 'Password set! Taking you to your dashboard...' : 'Password updated! Redirecting to login...'}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-200 mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showResetPw ? 'text' : 'password'}
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="At least 12 characters"
                  required
                  minLength={8}
                />
                <button type="button" onClick={() => setShowResetPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                  {showResetPw ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-200 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showResetPw ? 'text' : 'password'}
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Repeat your new password"
                  required
                />
                <button type="button" onClick={() => setShowResetPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                  {showResetPw ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && (
              <div className="p-3 bg-red-900/30 border border-red-800 rounded-lg">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className={`w-full flex items-center justify-center ${loading ? 'bg-gray-400 cursor-not-allowed' : 'btn-primary'}`}
            >
              {loading ? (
                <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>Updating...</>
              ) : 'Reset Password'}
            </button>
            <div className="text-center">
              <Link to="/" className="text-sm text-dark-muted hover:text-primary-600">Back to Login</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/** Wraps protected routes with idle timeout detection. Only active when authenticated. */
function IdleTimeoutWrapper({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  const handleTimeout = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      fetch(`${config.apiBaseUrl}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'timeout' }),
      }).catch(() => {});
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('userType');
    navigate('/', { replace: true });
  }, [navigate]);

  const { showWarning, secondsRemaining, stayLoggedIn } = useIdleTimeout(handleTimeout);

  // Only show if authenticated
  const isAuthenticated = !!localStorage.getItem('authToken');
  if (!isAuthenticated) return <>{children}</>;

  return (
    <>
      {children}
      {showWarning && (
        <IdleTimeoutWarning
          secondsRemaining={secondsRemaining}
          onStay={stayLoggedIn}
          onLogout={handleTimeout}
        />
      )}
    </>
  );
}

function RouteContentWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard/') || location.pathname === '/session-dashboard-preview';
  return <div className={isDashboard ? '' : 'pt-[100px]'}>{children}</div>;
}

function App() {
  const [showEarlyInterest, setShowEarlyInterest] = useState(false);
  const [eiForm, setEiForm] = useState({ name: '', email: '', country: '', phone: '' });
  const [eiInterests, setEiInterests] = useState<string[]>([]);
  const [eiCountries, setEiCountries] = useState<{ id: string; name: string }[]>([]);
  const [eiSubmitting, setEiSubmitting] = useState(false);
  const [eiSuccess, setEiSuccess] = useState(false);
  const [eiError, setEiError] = useState('');

  useEffect(() => {
    const handler = () => {
      setShowEarlyInterest(true);
      setEiSuccess(false);
      setEiError('');
      setEiForm({ name: '', email: '', country: '', phone: '' });
      setEiInterests([]);
    };
    window.addEventListener('open-early-interest', handler);
    return () => window.removeEventListener('open-early-interest', handler);
  }, []);

  // Open modal directly when ?early-access=1 is in the URL (shareable link)
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('early-access') === '1') {
      setShowEarlyInterest(true);
    }
  }, []);

  useEffect(() => {
    if (!showEarlyInterest || eiCountries.length > 0) return;
    fetch(`${config.apiBaseUrl}/countries`)
      .then(r => (r.ok ? r.json() : []))
      .then(setEiCountries)
      .catch(() => {});
  }, [showEarlyInterest, eiCountries.length]);

  const handleEiSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (eiInterests.length === 0) return;
    setEiSubmitting(true);
    setEiError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}/early-interest/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...eiForm, interests: eiInterests }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Submission failed');
      }
      setEiSuccess(true);
    } catch (err: any) {
      setEiError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setEiSubmitting(false);
    }
  };

  return (
    <>
      {showEarlyInterest && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-fuchsia-600 via-violet-600 to-indigo-600 px-8 py-4 flex items-center justify-between">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest bg-white/20 text-white px-2.5 py-0.5 rounded-full mb-1">Founding Coach Program</span>
                <h2 className="text-white text-xl font-bold">Request Early Access</h2>
                <p className="text-violet-100 text-xs mt-0.5">Free during beta. 30% off forever when paid plans launch. No credit card, no commitment.</p>
              </div>
            </div>

            <div className="px-8 py-5">
              {eiSuccess ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircleIcon className="w-7 h-7 text-emerald-500" />
                  </div>
                  <h3 className="text-gray-900 font-bold text-lg mb-1">You're on the list!</h3>
                  <p className="text-gray-500 text-sm">
                    {eiInterests.includes('beta_testing') && eiInterests.includes('demo')
                      ? "We'll be in touch soon to get you set up and schedule your demo."
                      : eiInterests.includes('beta_testing')
                      ? "We'll be in touch soon with your Founding Coach details."
                      : eiInterests.includes('demo')
                      ? "We'll reach out shortly to schedule your demo."
                      : "We'll keep you posted as CoachSight approaches launch."}
                  </p>
                  <button onClick={() => setShowEarlyInterest(false)} className="mt-4 text-sm font-medium text-violet-600 hover:text-violet-700">Close</button>
                </div>
              ) : (
                <form onSubmit={handleEiSubmit} className="space-y-4">
                  {/* Row 1: name + email */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                      <input type="text" required value={eiForm.name} onChange={e => setEiForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" placeholder="Jane Smith" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                      <input type="email" required value={eiForm.email} onChange={e => setEiForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" placeholder="jane@example.com" />
                    </div>
                  </div>

                  {/* Row 2: country + phone */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country of Practice <span className="text-red-500">*</span></label>
                      <select required value={eiForm.country} onChange={e => setEiForm(f => ({ ...f, country: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500">
                        <option value="" disabled>Select your country</option>
                        {(eiCountries.length > 0 ? eiCountries : [{ id: 'us', name: 'United States' }, { id: 'ca', name: 'Canada' }]).map(c => (
                          <option key={c.id} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-400 mt-0.5">US and Canada only for now.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input type="tel" value={eiForm.phone} onChange={e => setEiForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" placeholder="+1 555 000 0000" />
                    </div>
                  </div>

                  {/* Row 3: interests — horizontal checkboxes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">How would you like to join? <span className="text-red-500">*</span></label>
                    <div className="grid grid-cols-3 gap-3">
                      {([
                        { value: 'beta_testing', label: 'Join the beta', sub: 'Founding Coach — free during beta, 30% off forever' },
                        { value: 'demo',         label: 'Book a demo',   sub: "I'd like a walkthrough first" },
                        { value: 'updates_only', label: 'Keep me posted', sub: "Not ready yet, but interested" },
                      ] as const).map(({ value, label, sub }) => (
                        <label key={value} className="flex flex-col gap-1.5 cursor-pointer rounded-lg border border-gray-200 px-3 py-2.5 has-[:checked]:border-violet-500 has-[:checked]:bg-violet-50">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={eiInterests.includes(value)}
                              onChange={e => setEiInterests(prev =>
                                e.target.checked ? [...prev, value] : prev.filter(i => i !== value)
                              )}
                              className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                            />
                            <span className="text-sm font-semibold text-gray-900">{label}</span>
                          </div>
                          <span className="text-xs text-gray-500 leading-snug">{sub}</span>
                        </label>
                      ))}
                    </div>
                    {eiInterests.length === 0 && eiSubmitting && (
                      <p className="text-red-500 text-xs mt-1">Please select at least one option.</p>
                    )}
                  </div>

                  {eiError && <p className="text-red-600 text-sm">{eiError}</p>}
                  <div className="flex gap-3 pt-1">
                    <button type="button" onClick={() => setShowEarlyInterest(false)} className="px-5 py-2.5 border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium rounded-lg transition-colors text-sm">Cancel</button>
                    <button type="submit" disabled={eiSubmitting} className="flex-1 py-2.5 bg-gradient-to-r from-fuchsia-600 to-violet-600 hover:from-fuchsia-500 hover:to-violet-500 text-white font-bold rounded-lg transition-all disabled:opacity-50 text-sm">{eiSubmitting ? 'Submitting…' : 'Request Early Access →'}</button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    <HeaderProvider>
    <Router>
      <AppHeader />
      <RouteContentWrapper>
      <IdleTimeoutWrapper>
      <Routes>
        <Route path="/" element={<MarketingLandingPage />} />
        <Route path="/login" element={<LoginPage />} />

        <Route path="/register/provider" element={<ProviderRegistration />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify" element={<VerifyAccountPage />} />
        <Route path="/dashboard/coach" element={<ProtectedRoute><FeatureFlagProvider><CoachDashboard /></FeatureFlagProvider></ProtectedRoute>} />
        <Route path="/dashboard/coach/clients/:clientId" element={<ProtectedRoute><FeatureFlagProvider><ClientDetailPage /></FeatureFlagProvider></ProtectedRoute>} />
        <Route path="/dashboard/coach/session/:appointmentId" element={<ProtectedRoute><SessionCockpitPage /></ProtectedRoute>} />
        <Route path="/session-dashboard-preview" element={<ProtectedRoute><SessionDashboardPage /></ProtectedRoute>} />
        <Route path="/session/join" element={<ProtectedRoute><SessionJoinPage /></ProtectedRoute>} />
        <Route path="/dashboard/client" element={<ClientRoute><ClientDashboard /></ClientRoute>} />
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/fill-profile" element={<ProtectedRoute><FillProfile /></ProtectedRoute>} />
        <Route path="/sign/:token" element={<SigningPage />} />
        <Route path="/book" element={<BookingPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/dashboard/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/book/demo" element={<DemoBookingPage />} />
        <Route path="/book/:coachId" element={<CoachBookingPage />} />
        <Route path="/checkin/:token" element={<CheckInPublicForm />} />
        <Route path="/tour" element={<TourPage />} />
        <Route path="/terms" element={<TermsOfUsePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/ai-usage-data" element={<AIUsageDataPolicyPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/:category/:slug" element={<HelpPage />} />
        <Route path="/invoice/:token" element={<InvoicePublicPage />} />
        <Route path="/action-items/review/:token" element={<ActionItemReviewPage />} />
        <Route path="/notification-preferences" element={<NotificationPreferencesPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/stripe-return" element={<StripeReturn />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </IdleTimeoutWrapper>
      </RouteContentWrapper>
      <HelpWidget />
    </Router>
    </HeaderProvider>
    </>
  );
}

export default App;
